import { useMemo, useState } from 'react';
import {
  Calendar,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Clock,
  Inbox,
  Plus,
  Search,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { DatePicker } from '@/components/ui/DatePicker';
import { EmptyState } from '@/components/ui/EmptyState';
import { Modal } from '@/components/ui/Modal';
import { QueryError } from '@/components/ui/QueryError';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { Textarea } from '@/components/ui/Textarea';
import { useApiQuery } from '@/hooks/useApiQuery';
import { useRefreshKey } from '@/hooks/useRefreshKey';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { LEAVE_TYPE_LABEL } from '@/lib/leaveRequest.schema';
import { formatDate, formatDateTime } from '@/lib/format';
import { cn } from '@/lib/utils';
import { StaffLeaveModal } from '@/features/institute/staff/components/StaffLeaveModal';
import type { StaffLeavePayload } from '@/features/institute/staff/schemas/leave.schema';
import { myLeaveApi } from '../api/myLeave.api';
import { leaveRequestsApi } from '../api/leaveRequests.api';
import type {
  LeaveRequestStatus,
  LeaveRequestType,
  TeacherLeaveRequest,
} from '../types/leaveRequests';

type FilterTab = 'ALL' | 'PENDING' | 'APPROVED' | 'REJECTED';

const FILTER_TABS: readonly FilterTab[] = ['ALL', 'PENDING', 'APPROVED', 'REJECTED'];

/**
 * How each leave type is drawn. The words come from the form the family filled
 * in, so the teacher reads the reason they actually chose.
 */
const LEAVE_TYPE_CONFIG: Record<
  LeaveRequestType,
  { label: string; bg: string; text: string; border: string }
> = {
  MEDICAL: {
    label: LEAVE_TYPE_LABEL.MEDICAL,
    bg: 'bg-rose-500/10 dark:bg-rose-500/15',
    text: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-500/30',
  },
  FAMILY_EVENT: {
    label: LEAVE_TYPE_LABEL.FAMILY_EVENT,
    bg: 'bg-blue-500/10 dark:bg-blue-500/15',
    text: 'text-blue-600 dark:text-blue-400',
    border: 'border-blue-500/30',
  },
  CASUAL: {
    label: LEAVE_TYPE_LABEL.CASUAL,
    bg: 'bg-purple-500/10 dark:bg-purple-500/15',
    text: 'text-purple-600 dark:text-purple-400',
    border: 'border-purple-500/30',
  },
  TRAVEL: {
    label: LEAVE_TYPE_LABEL.TRAVEL,
    bg: 'bg-amber-500/10 dark:bg-amber-500/15',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/30',
  },
  OTHER: {
    label: LEAVE_TYPE_LABEL.OTHER,
    bg: 'bg-indigo-500/10 dark:bg-indigo-500/15',
    text: 'text-indigo-600 dark:text-indigo-400',
    border: 'border-indigo-500/30',
  },
};

const STATUS_CONFIG: Record<
  LeaveRequestStatus,
  { label: string; bg: string; text: string; border: string }
> = {
  PENDING: {
    label: 'Pending',
    bg: 'bg-amber-500/15 dark:bg-amber-500/20',
    text: 'text-amber-600 dark:text-amber-400',
    border: 'border-amber-500/30',
  },
  APPROVED: {
    label: 'Approved',
    bg: 'bg-emerald-500/15 dark:bg-emerald-500/20',
    text: 'text-emerald-600 dark:text-emerald-400',
    border: 'border-emerald-500/30',
  },
  REJECTED: {
    label: 'Rejected',
    bg: 'bg-rose-500/15 dark:bg-rose-500/20',
    text: 'text-rose-600 dark:text-rose-400',
    border: 'border-rose-500/30',
  },
  CANCELLED: {
    label: 'Withdrawn',
    bg: 'bg-slate-500/15 dark:bg-slate-500/20',
    text: 'text-slate-600 dark:text-slate-400',
    border: 'border-slate-500/30',
  },
};

/**
 * The screen's filters, as they sit in the address bar, so leaving the inbox
 * and coming back finds the same tab, search, class, section, type, day and
 * page.
 *
 * `date` is a day, `YYYY-MM-DD`: shows only the leave that covers it. Class
 * and section are read off the teacher's own requests; there is no fixed set
 * to check them against, and one that matches nothing simply lists nothing.
 */
type Filters = {
  status: FilterTab;
  search: string;
  class: string;
  section: string;
  type: string;
  date: string;
  page: number;
};

const DEFAULT_FILTERS: Filters = {
  status: 'PENDING',
  search: '',
  class: 'ALL',
  section: 'ALL',
  type: 'ALL',
  date: '',
  page: 1,
};

const FILTER_OPTIONS = {
  allowed: {
    status: FILTER_TABS,
    type: ['ALL', ...Object.keys(LEAVE_TYPE_CONFIG)],
  },
};

export function TeacherLeaveRequestsPage() {
  const [filters, setFilters] = useUrlFilters(DEFAULT_FILTERS, FILTER_OPTIONS);
  const {
    status: activeTab,
    search: searchQuery,
    class: selectedClass,
    section: selectedSection,
    type: selectedType,
    date: onDate,
    page: currentPage,
  } = filters;

  // Any change to what is listed starts the reader on page 1 again.
  function updateFilters(patch: Partial<Filters>) {
    setFilters({ ...patch, page: patch.page ?? 1 });
  }

  /** The teacher's own leave form — the one under Attendance → My leave. */
  const [isApplyingOwnLeave, setIsApplyingOwnLeave] = useState(false);

  // Decision Modal State
  /** Shown under the note when a rejection is sent without its reason. */
  const [noteError, setNoteError] = useState<string | null>(null);
  const [decisionModal, setDecisionModal] = useState<{
    request: TeacherLeaveRequest | null;
    action: 'APPROVED' | 'REJECTED' | null;
    note: string;
  }>({
    request: null,
    action: null,
    note: '',
  });

  const rowsPerPage = 7;

  // Selection Checkbox State
  const [selectedUuids] = useState<string[]>([]);
  const [isDeciding, setIsDeciding] = useState(false);

  /**
   * Changes on every arrival at this page and whenever the bell finds a new
   * notification pointing here, so a request a student has just sent appears
   * without a manual refresh. The list stays on screen while it reloads.
   */
  const refreshKey = useRefreshKey();

  // Fetch ALL requests from the API
  const { data: allRequests, isLoading, error, refetch } = useApiQuery(
    () => leaveRequestsApi.list(),
    [refreshKey],
  );

  // Dynamic KPI Metrics calculations
  const stats = useMemo(() => {
    const list = allRequests ?? [];
    const pendingCount = list.filter((r) => r.status === 'PENDING').length;
    const approvedCount = list.filter((r) => r.status === 'APPROVED').length;
    const rejectedCount = list.filter((r) => r.status === 'REJECTED').length;
    const totalCount = list.length;

    return {
      pending: pendingCount,
      approved: approvedCount,
      rejected: rejectedCount,
      total: totalCount,
    };
  }, [allRequests]);

  /*
    The class and section choices are the teacher's own — read off the
    requests, which the server already limits to the sections they are class
    teacher of — so every option leads to at least one row. The types are the
    labels the rows themselves wear.
  */
  const filterOptions = useMemo(() => {
    const sections = (allRequests ?? []).flatMap((r) => (r.section ? [r.section] : []));
    const classNames = [...new Set(sections.map((s) => s.className))].sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true }),
    );
    const sectionsOfClass = [
      ...new Map(
        sections.filter((s) => s.className === selectedClass).map((s) => [s.uuid, s]),
      ).values(),
    ].sort((a, b) => a.name.localeCompare(b.name));

    return {
      classes: [
        { value: 'ALL', label: 'All Classes' },
        ...classNames.map((name) => ({ value: name, label: name })),
      ],
      sections: [
        { value: 'ALL', label: selectedClass === 'ALL' ? 'Choose a class first' : 'All Sections' },
        ...sectionsOfClass.map((s) => ({ value: s.uuid, label: `Section ${s.name}` })),
      ],
      types: [
        { value: 'ALL', label: 'All Types' },
        ...(Object.keys(LEAVE_TYPE_CONFIG) as LeaveRequestType[]).map((type) => ({
          value: type,
          label: LEAVE_TYPE_CONFIG[type].label,
        })),
      ],
    };
  }, [allRequests, selectedClass]);

  // Filter and search requests
  const filteredRequests = useMemo(() => {
    let list = allRequests ?? [];

    // Tab Status Filter
    if (activeTab !== 'ALL') {
      list = list.filter((r) => r.status === activeTab);
    }

    // Leave Type Filter
    if (selectedType !== 'ALL') {
      list = list.filter((r) => r.type === selectedType);
    }

    if (selectedClass !== 'ALL') {
      list = list.filter((r) => r.section?.className === selectedClass);
    }
    if (selectedSection !== 'ALL') {
      list = list.filter((r) => r.section?.uuid === selectedSection);
    }

    // Leave that covers the chosen day. `YYYY-MM-DD` compares as text.
    if (onDate) {
      list = list.filter((r) => r.fromDate <= onDate && onDate <= r.toDate);
    }

    // Search Query Filter
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (r) =>
          r.student?.fullName.toLowerCase().includes(q) ||
          r.reason.toLowerCase().includes(q) ||
          r.student?.rollNumber?.toLowerCase().includes(q) ||
          (r.section && `${r.section.className} - ${r.section.name}`.toLowerCase().includes(q)),
      );
    }

    // Show new / pending requests at the top, then newest requestedOn first
    return [...list].sort((a, b) => {
      if (a.status === 'PENDING' && b.status !== 'PENDING') return -1;
      if (a.status !== 'PENDING' && b.status === 'PENDING') return 1;
      const timeA = new Date(a.requestedOn).getTime() || 0;
      const timeB = new Date(b.requestedOn).getTime() || 0;
      return timeB - timeA;
    });
  }, [allRequests, activeTab, selectedType, selectedClass, selectedSection, onDate, searchQuery]);

  // Paginated requests
  const paginatedRequests = useMemo(() => {
    const startIndex = (currentPage - 1) * rowsPerPage;
    return filteredRequests.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredRequests, currentPage, rowsPerPage]);

  const totalPages = Math.ceil(filteredRequests.length / rowsPerPage) || 1;

  /** Closes the decision popup and forgets anything typed or flagged in it. */
  function closeDecision() {
    setDecisionModal({ request: null, action: null, note: '' });
    setNoteError(null);
  }

  // Handle Decision Submit (Approve / Reject)
  const handleDecisionSubmit = async () => {
    if (!decisionModal.request || !decisionModal.action) return;

    // A rejection carries its reason to the family — the server refuses one
    // without, so the popup asks before sending.
    if (decisionModal.action === 'REJECTED' && decisionModal.note.trim().length < 3) {
      setNoteError('Give the family a reason for rejecting this leave');
      return;
    }
    setNoteError(null);

    setIsDeciding(true);
    const { request, action, note } = decisionModal;

    try {
      await leaveRequestsApi.decide(request.uuid, {
        status: action,
        ...(note.trim() ? { note: note.trim() } : {}),
      });

      const name = request.student?.fullName ?? 'Student';
      toast.success(
        action === 'APPROVED' ? `${name}'s leave is approved` : `${name}'s request was rejected`,
        action === 'APPROVED'
          ? 'Remember to mark those days as leave when you take the register.'
          : 'The decision has been recorded and the family is notified.',
      );

      closeDecision();
      refetch();
    } catch (caught) {
      toast.error(
        'Could not save decision',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsDeciding(false);
    }
  };

  async function applyForOwnLeave(payload: StaffLeavePayload) {
    try {
      await myLeaveApi.apply(payload);
      toast.success(
        'Leave request sent',
        'The school office will approve or refuse it, and you will be told either way. Follow it under Attendance → My leave.',
      );
    } catch (caught) {
      throw caught instanceof ApiRequestError
        ? new Error(caught.message)
        : new Error('Something went wrong. Please try again.');
    }
  }

  return (
    <div className="flex flex-col gap-7">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black tracking-tight text-ink sm:text-2xl">
            Leave Requests
          </h1>
          <p className=" text-sm font-medium text-ink-2">
            Manage leave requests from parents for their children. Approve or reject requests with
            comments.
          </p>
        </div>
        {/* The teacher's own leave, not a child's: a family asks for that from
            the parent panel. */}
        <Button
          size="lg"
          className="font-bold text-[14px]"
          leftIcon={<Plus className="h-4 w-4" aria-hidden />}
          onClick={() => setIsApplyingOwnLeave(true)}
        >
          Request Leave
        </Button>
      </div>

      <StaffLeaveModal
        isOpen={isApplyingOwnLeave}
        staffName="you"
        isOwn
        onClose={() => setIsApplyingOwnLeave(false)}
        onSave={applyForOwnLeave}
      />

      {/* 4 KPI Top Stat Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* 1. Pending Requests */}
        <div className="card p-5 bg-gradient-to-br from-amber-50/80 via-amber-50/30 to-orange-50/60 dark:from-amber-950/30 dark:via-amber-900/10 dark:to-orange-950/20 border border-amber-200/80 dark:border-amber-800/40 rounded-2xl flex items-center gap-4 shadow-xs hover:shadow-md hover:shadow-amber-500/10 transition-all">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-amber-500 to-orange-500 text-white shadow-md shadow-amber-500/25 ring-2 ring-amber-500/20 flex items-center justify-center shrink-0">
            <Clock className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-2xl sm:text-3xl font-black text-amber-700 dark:text-amber-300 leading-tight block">
              {stats.pending}
            </span>
            <span className="text-sm font-bold text-amber-900/70 dark:text-amber-300/80 mt-0.5 block">
              Pending Requests
            </span>
          </div>
        </div>

        {/* 2. Approved Requests */}
        <div className="card p-5 bg-gradient-to-br from-emerald-50/80 via-emerald-50/30 to-teal-50/60 dark:from-emerald-950/30 dark:via-emerald-900/10 dark:to-teal-950/20 border border-emerald-200/80 dark:border-emerald-800/40 rounded-2xl flex items-center gap-4 shadow-xs hover:shadow-md hover:shadow-emerald-500/10 transition-all">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-emerald-500 to-teal-600 text-white shadow-md shadow-emerald-500/25 ring-2 ring-emerald-500/20 flex items-center justify-center shrink-0">
            <CheckCircle2 className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-2xl sm:text-3xl font-black text-emerald-700 dark:text-emerald-300 leading-tight block">
              {stats.approved}
            </span>
            <span className="text-sm font-bold text-emerald-900/70 dark:text-emerald-300/80 mt-0.5 block">
              Approved
            </span>
          </div>
        </div>

        {/* 3. Rejected Requests */}
        <div className="card p-5 bg-gradient-to-br from-rose-50/80 via-rose-50/30 to-red-50/60 dark:from-rose-950/30 dark:via-rose-900/10 dark:to-red-950/20 border border-rose-200/80 dark:border-rose-800/40 rounded-2xl flex items-center gap-4 shadow-xs hover:shadow-md hover:shadow-rose-500/10 transition-all">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-rose-500 to-red-600 text-white shadow-md shadow-rose-500/25 ring-2 ring-rose-500/20 flex items-center justify-center shrink-0">
            <XCircle className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-2xl sm:text-3xl font-black text-rose-700 dark:text-rose-300 leading-tight block">
              {stats.rejected}
            </span>
            <span className="text-sm font-bold text-rose-900/70 dark:text-rose-300/80 mt-0.5 block">
              Rejected
            </span>
          </div>
        </div>

        {/* 4. Total Requests */}
        <div className="card p-5 bg-gradient-to-br from-blue-50/80 via-indigo-50/30 to-sky-50/60 dark:from-blue-950/30 dark:via-indigo-900/10 dark:to-sky-950/20 border border-blue-200/80 dark:border-indigo-800/40 rounded-2xl flex items-center gap-4 shadow-xs hover:shadow-md hover:shadow-blue-500/10 transition-all">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-tr from-blue-600 via-indigo-600 to-sky-600 text-white shadow-md shadow-indigo-500/25 ring-2 ring-indigo-500/20 flex items-center justify-center shrink-0">
            <Calendar className="w-6 h-6 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-2xl sm:text-3xl font-black text-indigo-700 dark:text-indigo-300 leading-tight block">
              {stats.total}
            </span>
            <span className="text-sm font-bold text-indigo-900/70 dark:text-indigo-300/80 mt-0.5 block">
              Total Requests
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Filter Bar (All, Pending, Approved, Rejected) */}
      <div className="flex items-center gap-6 md:gap-8 border-b border-line overflow-x-auto no-scrollbar pt-2">
        <button
          type="button"
          onClick={() => {
            updateFilters({ status: 'ALL' });
          }}
          className={cn(
            'flex items-center gap-2 pb-3.5 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
            activeTab === 'ALL'
              ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
              : 'text-ink-3 hover:text-ink',
          )}
        >
          <span>All Requests</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-surface-2 border border-line text-ink-2">
            {stats.total}
          </span>
          {activeTab === 'ALL' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            updateFilters({ status: 'PENDING' });
          }}
          className={cn(
            'flex items-center gap-2 pb-3.5 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
            activeTab === 'PENDING'
              ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
              : 'text-ink-3 hover:text-ink',
          )}
        >
          <span>Pending</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/30">
            {stats.pending}
          </span>
          {activeTab === 'PENDING' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            updateFilters({ status: 'APPROVED' });
          }}
          className={cn(
            'flex items-center gap-2 pb-3.5 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
            activeTab === 'APPROVED'
              ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
              : 'text-ink-3 hover:text-ink',
          )}
        >
          <span>Approved</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border border-emerald-500/30">
            {stats.approved}
          </span>
          {activeTab === 'APPROVED' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
          )}
        </button>

        <button
          type="button"
          onClick={() => {
            updateFilters({ status: 'REJECTED' });
          }}
          className={cn(
            'flex items-center gap-2 pb-3.5 text-base font-bold transition-all whitespace-nowrap relative cursor-pointer',
            activeTab === 'REJECTED'
              ? 'text-indigo-600 dark:text-indigo-400 font-extrabold'
              : 'text-ink-3 hover:text-ink',
          )}
        >
          <span>Rejected</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-rose-500/15 text-rose-700 dark:text-rose-400 border border-rose-500/30">
            {stats.rejected}
          </span>
          {activeTab === 'REJECTED' && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 dark:bg-indigo-400 rounded-full" />
          )}
        </button>
      </div>

      {/* Filter and Search Bar Controls */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
        {/* Search Input (5 cols) */}
        <div className="md:col-span-4 relative">
          <Search className="w-4 h-4 text-ink-3 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
          <input
            type="text"
            placeholder="Search by student, roll no., class or reason..."
            value={searchQuery}
            onChange={(e) => {
              updateFilters({ search: e.target.value });
            }}
            className="w-full bg-surface border border-line rounded-xl pl-10 pr-4 py-2.5 text-sm text-ink placeholder:text-ink-3 focus:outline-none focus:border-indigo-500 shadow-2xs"
          />
        </div>

        {/* Class Filter (2 cols) */}
        <div className="md:col-span-2">
          <Select
            label="Class"
            hideLabel
            hideSearch
            options={filterOptions.classes}
            value={selectedClass}
            onChange={(e) => {
              // A section belongs to one class, so changing the class clears it.
              updateFilters({ class: e.target.value, section: 'ALL' });
            }}
          />
        </div>

        {/* Section Filter (2 cols) */}
        <div className="md:col-span-2">
          <Select
            label="Section"
            hideLabel
            hideSearch
            options={filterOptions.sections}
            value={selectedSection}
            disabled={selectedClass === 'ALL'}
            onChange={(e) => {
              updateFilters({ section: e.target.value });
            }}
          />
        </div>

        {/* Leave Type Filter (2 cols) */}
        <div className="md:col-span-2">
          <Select
            label="Leave Type"
            hideLabel
            hideSearch
            options={filterOptions.types}
            value={selectedType}
            onChange={(e) => {
              updateFilters({ type: e.target.value });
            }}
          />
        </div>

        {/* Date Range Button / Picker (2 cols) */}
        <div className="md:col-span-2">
          <DatePicker
            label="On leave on"
            hideLabel
            placeholder="On leave on…"
            value={onDate}
            onChange={(e) => {
              updateFilters({ date: e.target.value });
            }}
          />
        </div>
      </div>

      {/* Loading & Error States */}
      {error && (
        <QueryError
          title="Could not load your leave requests"
          error={error}
          onRetry={refetch}
        />
      )}

      {isLoading && !allRequests && <SkeletonLines count={6} />}

      {/* Main Leave Requests Table */}
      {!isLoading && filteredRequests.length === 0 ? (
        <div className="card p-8 text-center bg-surface border border-line rounded-2xl shadow-xs">
          <EmptyState
            icon={<Inbox className="h-6 w-6 text-ink-3" />}
            title={activeTab === 'PENDING' ? 'No pending requests' : 'No leave requests found'}
            description={
              activeTab === 'PENDING'
                ? 'When a family asks for a student to be excused, it appears here.'
                : 'No requests matched your current search and filter criteria.'
            }
          />
        </div>
      ) : (
        <div className="card bg-surface border border-line rounded-2xl overflow-hidden shadow-xs">
          <div className="overflow-x-auto relative">
            <table className="w-full text-left text-sm border-separate border-spacing-0">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-ink-3 font-bold text-xs uppercase tracking-wider">
                 
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">Student</th>
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">Leave Type</th>
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">From Date</th>
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">To Date</th>
                  <th className="py-3.5 px-4 max-w-sm border-b border-line">Days &amp; Reason</th>
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">Requested On</th>
                  <th className="py-3.5 px-4 border-b border-line whitespace-nowrap">Status</th>
                  <th className="py-3.5 px-4 text-right sticky right-0 z-20 bg-surface-2 border-b border-l border-line whitespace-nowrap">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line font-medium">
                {paginatedRequests.map((req) => {
                  const typeCfg = LEAVE_TYPE_CONFIG[req.type] || LEAVE_TYPE_CONFIG.OTHER;
                  const statusCfg = STATUS_CONFIG[req.status] || STATUS_CONFIG.PENDING;
                  const studentName = req.student?.fullName ?? 'Student';
                  const initials = studentName
                    .split(' ')
                    .map((n) => n[0])
                    .join('')
                    .slice(0, 2)
                    .toUpperCase();

                  const isSelected = selectedUuids.includes(req.uuid);

                  return (
                    <tr
                      key={req.uuid}
                      className={cn(
                        'group transition-colors',
                        isSelected
                          ? 'bg-indigo-50/60 dark:bg-indigo-950/40'
                          : 'hover:bg-surface-2/70',
                      )}
                    >
                      

                      {/* Student details with Avatar, Roll No & Class/Section */}
                      <td className="py-3.5 px-4 border-b border-line whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="relative shrink-0">
                            <span
                              className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 via-indigo-600 to-sky-600 text-white flex items-center justify-center font-bold text-xs shrink-0 shadow-2xs"
                              aria-hidden
                            >
                              {initials}
                            </span>
                            {req.status === 'PENDING' && (
                              <span
                                className="absolute bottom-0 right-0 h-2.5 w-2.5 rounded-full bg-emerald-500 ring-2 ring-surface shadow-2xs"
                                title="New leave request"
                                aria-label="New leave request"
                              />
                            )}
                          </div>
                          <div>
                            <span className="font-bold text-ink text-base block leading-tight">
                              {studentName}
                            </span>
                            <span className="text-sm text-ink-3 block mt-0.5">
                              Roll No. {req.student?.rollNumber || '—'}
                            </span>
                            {req.section && (
                              <span className="text-sm text-ink-3 block font-semibold mt-0.5">
                                {req.section.className} - Section {req.section.name}
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Leave Type Badge */}
                      <td className="py-3.5 px-4 border-b border-line">
                        <span
                          className={cn(
                            'px-2.5 py-1 rounded-pill text-sm font-black border inline-block whitespace-nowrap shadow-2xs',
                            typeCfg.bg,
                            typeCfg.text,
                            typeCfg.border,
                          )}
                        >
                          {typeCfg.label}
                        </span>
                      </td>

                      {/* From Date */}
                      <td className="py-3.5 px-4 border-b border-line whitespace-nowrap text-ink-2 font-semibold">
                        {formatDate(req.fromDate)}
                      </td>

                      {/* To Date */}
                      <td className="py-3.5 px-4 border-b border-line whitespace-nowrap text-ink-2 font-semibold">
                        {formatDate(req.toDate)}
                      </td>

                      {/* Days & Reason Combined */}
                      <td className="py-3.5 px-4 border-b border-line max-w-sm">
                        <div>
                          <span className="font-bold text-ink text-sm block">
                            {req.days} {req.days === 1 ? 'day' : 'days'}
                          </span>
                          {/* Two lines in the table, the whole of it on hover — the
                              column is the only place the reason is shown. */}
                          <p
                            className="line-clamp-2 whitespace-normal text-sm text-ink-2 font-medium mt-0.5"
                            title={req.reason}
                          >
                            {req.reason}
                          </p>
                        </div>
                      </td>

                      {/* Requested On */}
                      <td className="py-3.5 px-4 border-b border-line whitespace-nowrap text-sm text-ink-3">
                        {formatDateTime(req.requestedOn)}
                      </td>

                      {/* Status */}
                      <td className="py-3.5 px-4 border-b border-line">
                        <span
                          className={cn(
                            'px-2.5 py-1 rounded-pill text-sm font-black border inline-flex items-center gap-1.5 whitespace-nowrap shadow-2xs',
                            statusCfg.bg,
                            statusCfg.text,
                            statusCfg.border,
                          )}
                        >
                          {req.status === 'PENDING' && (
                            <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                          )}
                          {statusCfg.label}
                        </span>
                        {/* Who decided and why — kept beside the status now that
                            there is no separate details window to hold it. */}
                        {req.decidedOn && (
                          <p className="mt-1 text-xs text-ink-3 whitespace-nowrap">
                            {req.decidedBy && `by ${req.decidedBy} · `}
                            {formatDate(req.decidedOn)}
                          </p>
                        )}
                        {req.decisionNote && (
                          <p
                            className="mt-0.5 max-w-[16rem] line-clamp-2 whitespace-normal text-xs italic text-ink-2"
                            title={req.decisionNote}
                          >
                            “{req.decisionNote}”
                          </p>
                        )}
                      </td>

                      {/* Sticky Actions */}
                      <td
                        className={cn(
                          'py-3.5 px-4 text-right sticky right-0 z-10 border-b border-l border-line whitespace-nowrap transition-colors',
                          isSelected
                            ? 'bg-[#eef2ff] dark:bg-[#1a1c38]'
                            : 'bg-surface group-hover:bg-surface-2',
                        )}
                      >
                        {req.status === 'PENDING' ? (
                          <div className="flex items-center justify-end gap-1.5">
                            {/* Approve Button */}
                            <button
                              type="button"
                              onClick={() =>
                                setDecisionModal({
                                  request: req,
                                  action: 'APPROVED',
                                  note: '',
                                })
                              }
                              className="w-8 h-8 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/25 border border-emerald-500/30 text-emerald-600 dark:text-emerald-400 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                              title="Approve Leave Request"
                              aria-label={`Approve ${req.student?.fullName ?? 'this student'}'s leave`}
                            >
                              <Check className="w-4 h-4 stroke-[2.5]" />
                            </button>

                            {/* Reject Button */}
                            <button
                              type="button"
                              onClick={() =>
                                setDecisionModal({
                                  request: req,
                                  action: 'REJECTED',
                                  note: '',
                                })
                              }
                              className="w-8 h-8 rounded-lg bg-rose-500/10 hover:bg-rose-500/25 border border-rose-500/30 text-rose-600 dark:text-rose-400 flex items-center justify-center transition-colors cursor-pointer shadow-2xs"
                              title="Reject Leave Request"
                              aria-label={`Reject ${req.student?.fullName ?? 'this student'}'s leave`}
                            >
                              <X className="w-4 h-4 stroke-[2.5]" />
                            </button>
                          </div>
                        ) : (
                          <span className="text-sm text-ink-3" aria-label="Already decided">
                            —
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table Footer with Pagination */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-t border-line bg-surface-2/40 text-xs font-semibold text-ink-3">
            <div>
              Showing{' '}
              <span className="text-ink font-bold">
                {filteredRequests.length > 0 ? (currentPage - 1) * rowsPerPage + 1 : 0}
              </span>{' '}
              to{' '}
              <span className="text-ink font-bold">
                {Math.min(currentPage * rowsPerPage, filteredRequests.length)}
              </span>{' '}
              of <span className="text-ink font-bold">{filteredRequests.length}</span> requests
            </div>

            <div className="flex items-center gap-1.5">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setFilters({ page: 1 })}
                className="p-1.5 rounded-lg border border-line bg-surface hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>

              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setFilters({ page: Math.max(1, currentPage - 1) })}
                className="p-1.5 rounded-lg border border-line bg-surface hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setFilters({ page })}
                  className={cn(
                    'w-8 h-8 rounded-lg text-xs font-bold transition-all cursor-pointer',
                    currentPage === page
                      ? 'bg-indigo-600 text-white shadow-xs'
                      : 'border border-line bg-surface text-ink-2 hover:bg-surface-3 hover:text-ink',
                  )}
                >
                  {page}
                </button>
              ))}

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setFilters({ page: Math.min(totalPages, currentPage + 1) })}
                className="p-1.5 rounded-lg border border-line bg-surface hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>

              <button
                type="button"
                disabled={currentPage === totalPages}
                onClick={() => setFilters({ page: totalPages })}
                className="p-1.5 rounded-lg border border-line bg-surface hover:bg-surface-3 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Decision Action Modal (Approve / Reject Note) */}
      <Modal
        isOpen={Boolean(decisionModal.request)}
        onClose={() => closeDecision()}
        title={
          decisionModal.action === 'APPROVED' ? 'Approve Leave Request' : 'Reject Leave Request'
        }
        description={`For student ${decisionModal.request?.student?.fullName ?? 'Student'}`}
        footer={
          <div className="flex items-center justify-end gap-2.5">
            <Button
              variant="secondary"
              onClick={() => closeDecision()}
            >
              Cancel
            </Button>
            <Button
              isLoading={isDeciding}
              variant={decisionModal.action === 'APPROVED' ? 'primary' : 'danger'}
              onClick={handleDecisionSubmit}
            >
              {decisionModal.action === 'APPROVED' ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {/* What the family sent, laid out as label and value — the reason
              keeps the parent's own line breaks and wraps however long it is. */}
          {decisionModal.request && (
            <dl className="grid grid-cols-1 gap-x-4 gap-y-3 rounded-xl border border-line bg-surface-2 p-4 text-sm sm:grid-cols-2">
              <div className="min-w-0">
                <dt className="lbl">Student</dt>
                <dd className="mt-1 font-[560] text-ink break-words">
                  {decisionModal.request.student?.fullName ?? '—'}
                  {decisionModal.request.student?.rollNumber && (
                    <span className="font-normal text-ink-3">
                      {' '}
                      · Roll {decisionModal.request.student.rollNumber}
                    </span>
                  )}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="lbl">Class</dt>
                <dd className="mt-1 font-[560] text-ink">
                  {decisionModal.request.section
                    ? `${decisionModal.request.section.className} ${decisionModal.request.section.name}`
                    : '—'}
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="lbl">Leave period</dt>
                <dd className="mt-1 font-[560] text-ink">
                  {decisionModal.request.fromDate === decisionModal.request.toDate
                    ? formatDate(decisionModal.request.fromDate)
                    : `${formatDate(decisionModal.request.fromDate)} – ${formatDate(decisionModal.request.toDate)}`}
                  <span className="font-normal text-ink-3">
                    {' '}
                    · {decisionModal.request.days} {decisionModal.request.days === 1 ? 'day' : 'days'}
                  </span>
                </dd>
              </div>
              <div className="min-w-0">
                <dt className="lbl">Type</dt>
                <dd className="mt-1">
                  <span
                    className={`inline-flex rounded-pill border px-2 py-0.5 text-xs font-bold ${LEAVE_TYPE_CONFIG[decisionModal.request.type].bg} ${LEAVE_TYPE_CONFIG[decisionModal.request.type].text} ${LEAVE_TYPE_CONFIG[decisionModal.request.type].border}`}
                  >
                    {LEAVE_TYPE_CONFIG[decisionModal.request.type].label}
                  </span>
                </dd>
              </div>
              <div className="min-w-0 sm:col-span-2">
                <dt className="lbl">Requested on</dt>
                <dd className="mt-1 text-ink-2">{formatDateTime(decisionModal.request.requestedOn)}</dd>
              </div>
              <div className="min-w-0 border-t border-line pt-3 sm:col-span-2">
                <dt className="lbl">Reason from the parent</dt>
                <dd className="mt-1 whitespace-pre-line break-words leading-relaxed text-ink">
                  {decisionModal.request.reason}
                </dd>
              </div>
            </dl>
          )}

          <Textarea
            label={
              decisionModal.action === 'REJECTED' ? (
                <>
                  Reason for rejection <span className="text-red-500">*</span>
                </>
              ) : (
                'Note for the family (optional)'
              )
            }
            rows={3}
            maxLength={255}
            placeholder={
              decisionModal.action === 'REJECTED'
                ? 'Tell the family why — they will see this beside the decision.'
                : 'Any instruction for the family, if needed.'
            }
            value={decisionModal.note}
            error={noteError ?? undefined}
            onChange={(e) => {
              setDecisionModal((prev) => ({ ...prev, note: e.target.value }));
              if (noteError) setNoteError(null);
            }}
          />
        </div>
      </Modal>

    </div>
  );
}
